"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { getDeliveryQueryTenantId } from "@/hooks/queries/delivery-integrations"
import { metaAdsIntegrationQueryKey } from "@/hooks/queries/meta-ads"
import type {
  MetaAdsFormScope,
  MetaAdsIntegrationInput,
  MetaAdsIntegrationView,
  MetaFormFollowUpActionsInput,
} from "@/lib/meta-ads/types"
import { withQueryHandling } from "@/lib/with-query-handling"
import {
  acknowledgeMetaAdsFormHandoffAction,
  beginMetaAdsOAuthAction,
  checkMetaAdsConnectionAction,
  completeMetaAdsOAuthAction,
  createMetaAdsFormCopyAction,
  disconnectMetaAdsIntegrationAction,
  sendMetaAdsTestLeadAction,
  saveMetaAdsFormFollowUpActionsAction,
} from "@/server-actions/meta-ads"

type FormErrorSetter = (errors: Record<string, unknown>) => void

export function useSaveMetaAdsFormFollowUpActionsMutation() {
  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async (input: MetaFormFollowUpActionsInput) => {
      const result = await withQueryHandling(saveMetaAdsFormFollowUpActionsAction(input), {
        loading: "Saving Instant Form follow-up…", success: "Follow-up actions saved",
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.set,
  })
}

export function useBeginMetaAdsOAuthMutation() {
  return useMutation({
    mutationFn: async (input: MetaAdsIntegrationInput & { setErrors?: FormErrorSetter }) => {
      const { setErrors, ...values } = input
      const result = await withQueryHandling(beginMetaAdsOAuthAction(values), {
        loading: "Preparing Meta authorization…",
        showErrorToast: false,
        setErrors,
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useAcknowledgeMetaAdsFormHandoffMutation() {
  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async (copiedFormId: string) => {
      const result = await withQueryHandling(acknowledgeMetaAdsFormHandoffAction({
        copiedFormId,
      }), {
        loading: "Re-checking the copied Instant Form…",
        success: "Live-ad update confirmation recorded",
        showErrorToast: false,
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.set,
  })
}

export function useCompleteMetaAdsOAuthMutation() {
  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async (input: {
      sessionId: string
      pageId: string
      adAccountId: string
      phoneFieldKey: string
      formScope: MetaAdsFormScope
      selectedFormIds: string[]
    }) => {
      const result = await withQueryHandling(completeMetaAdsOAuthAction(input), {
        loading: "Connecting the selected Facebook Page…",
        success: "Meta Ads connected",
        showErrorToast: false,
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: result => cache.set(result.integration),
  })
}

export function useCreateMetaAdsFormCopyMutation() {
  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async (input: { sourceFormId: string; name: string }) => {
      const result = await withQueryHandling(createMetaAdsFormCopyAction(input), {
        loading: "Creating a Versafy-ready Instant Form…",
        success: "Unrestricted Instant Form created",
        showErrorToast: false,
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: result => cache.set(result.integration),
  })
}

export function useCheckMetaAdsMutation() {
  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async () => {
      const result = await withQueryHandling(checkMetaAdsConnectionAction({}), {
        loading: "Checking Meta Ads connection…",
        success: value => value.status === "connected"
          ? "Meta Ads is connected"
          : "Meta Ads setup still needs attention",
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.set,
  })
}

export function useSendMetaAdsTestLeadMutation() {
  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async (input: { phoneNumber: string; setErrors?: FormErrorSetter }) => {
      const { setErrors, ...values } = input
      const result = await withQueryHandling(sendMetaAdsTestLeadAction(values), {
        loading: "Sending a Meta test lead…",
        success: "Test lead sent. Waiting for its callback task…",
        showErrorToast: false,
        setErrors,
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.set,
  })
}

export function useDisconnectMetaAdsMutation() {
  const cache = useMetaAdsCache()
  return useMutation({
    mutationFn: async () => {
      const result = await withQueryHandling(disconnectMetaAdsIntegrationAction({}), {
        loading: "Disconnecting Meta Ads and deleting its data…",
        success: "Meta Ads disconnected and its data deleted",
      })
      if ("errorMessage" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: () => cache.invalidate(),
  })
}

function useMetaAdsCache() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const queryKey = metaAdsIntegrationQueryKey(getDeliveryQueryTenantId(user))
  return {
    set: (value: MetaAdsIntegrationView) => queryClient.setQueryData(queryKey, value),
    invalidate: () => queryClient.invalidateQueries({ queryKey }),
  }
}
